import { useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import ButtonGroup from 'react-bootstrap/ButtonGroup';

import * as K from 'common/constants';
import ELEMENT, { createTestId } from 'common/element';
import { HELP_ROUTE } from 'components/help/constants';
import { UrlParams } from 'common/types';
import { useSetSearchParams } from 'hooks/useSetSearchParams';
import log from 'common/ScalyrLogger';

import OverlayTrigger from 'components/common/OverlayTrigger/OverlayTrigger';
import TimeRangeDropdown from 'components/common/TimeRangeDropdown/TimeRangeDropdown';
import Button, { ButtonVariant } from 'components/common/Button/Button';
import { CommandInfo, HelpItem } from 'components/search/SearchHelper/help';
import { draftSearchParamState } from 'components/common/Managers/RouteStateManager.state';
import {
  TimeRangeDropdownValidationStatus,
  useTimeRangeDropdownState,
} from 'components/common/TimeRangeDropdown/TimeRangeDropdown.hooks';
import CrossTeamSearch from 'components/search/CrossTeamSearch/CrossTeamSearch';
import SearchHelpPanel from 'components/search/SearchHelper/SearchHelpPanel';
import GraphStyleDropdown from 'components/common/Graphs/GraphStyleDropdown/GraphStyleDropdown';
import Toolbar from 'components/common/Toolbar/Toolbar';
import { CROSS_TEAM_SEARCH_ROUTES, routeState } from 'components/common/Managers/RouteStateManager.state';
import ViewSelector from 'components/search/ViewSelector/ViewSelector';
import { accountDataState } from 'components/account/account.state';

import { CopyToClipboardHandler, DownloadFileHandler } from '../../PowerQueriesPage';
import PowerQueryInput from './PowerQueryInput';
import PowerQueryActionsDropdown from './PowerQueryActionsDropdown';

type PowerQueryControlsProps = {
  copyToClipboardHandler: CopyToClipboardHandler;
  downloadFileHandler: DownloadFileHandler;
  disableCopyAndDownload: boolean;
};

const PowerQueryControls = ({
  copyToClipboardHandler,
  downloadFileHandler,
  disableCopyAndDownload,
}: PowerQueryControlsProps) => {
  const [showOverlay, setShowOverlay] = useState<boolean>(false);
  const [helpCommand, setHelpCommand] = useState<string>('');
  const setSearchParams = useSetSearchParams();
  const [draftParams, setDraftParams] = useRecoilState(draftSearchParamState);
  const draftFilter = draftParams[K.URL_PARAM.FILTER];

  const route = useRecoilValue(routeState);
  const isCrossTeamSearchEnabled = CROSS_TEAM_SEARCH_ROUTES.includes(route);
  const account = useRecoilValue(accountDataState);

  const { handleInputValidation: handleTimeRangeDropdownInputCheck } = useTimeRangeDropdownState();

  const handleInputChange = (newValue: string): void => {
    setDraftParams((state) => ({ ...state, [K.URL_PARAM.FILTER]: newValue }));
  };

  // set up the content of the help panel
  const helpInfo = CommandInfo.find((item: HelpItem) => item.name === helpCommand);
  let helpPanel = <></>;
  if (showOverlay && helpInfo) {
    const helpLink = [HELP_ROUTE.HELP_POWER_QUERIES, helpInfo?.name].join('#');
    helpPanel = <SearchHelpPanel item={helpInfo} helpLink={helpLink} close={(): void => setShowOverlay(false)} />;
  }

  const showHelpPanel = (command: string): void => {
    setHelpCommand(command);
    setShowOverlay(true);
  };

  const commandItems = CommandInfo.map((info, index) => (
    <div key={info.name}>
      <li
        className="PowerQueries__CommandItem"
        onClick={(): void => showHelpPanel(info.name)}
        data-test-id={createTestId(ELEMENT.powerQueryCommand, info.name)}
        key={info.name}
      >
        {info.name}
      </li>
      {index < CommandInfo.length - 1 && <span className="PowerQueries__CommandDot">•</span>}
    </div>
  ));

  const commandBar = (
    <div>
      <ul className="PowerQueries__CommandList">{commandItems}</ul>
    </div>
  );
  const helperEl = (
    <OverlayTrigger
      placement="bottom-start"
      overlay={helpPanel}
      showOverlay={showOverlay}
      setShowOverlay={setShowOverlay}
      disabled
    >
      <div>{commandBar}</div>
    </OverlayTrigger>
  );

  const invokeQuery = (): void => {
    const { status } = handleTimeRangeDropdownInputCheck();
    if (status === TimeRangeDropdownValidationStatus.ERROR) {
      log.debug("/query page: Search won't be issued, invalid input in TimeRangeDropdown");
      return;
    }
    setSearchParams({ [K.URL_PARAM.FILTER]: draftFilter || '' } as UrlParams, true);
  };

  const leftItems = [];
  const ctsButtonClass = account.isSingularityXDRUser ? undefined : 'CrossTeamSearch__RoundedRightCorners';
  const crossTeamSearchButton = isCrossTeamSearchEnabled ? <CrossTeamSearch className={ctsButtonClass} /> : null;
  const viewButton = account.isSingularityXDRUser ? (
    <ViewSelector crossTeamSearchEnabled={isCrossTeamSearchEnabled} />
  ) : null;
  if (crossTeamSearchButton && viewButton) {
    leftItems.push(
      <ButtonGroup>
        {crossTeamSearchButton}
        {viewButton}
      </ButtonGroup>,
    );
  } else if (crossTeamSearchButton) {
    leftItems.push(crossTeamSearchButton);
  } else if (viewButton) {
    leftItems.push(viewButton);
  }
  leftItems.push(helperEl);

  const rightItems = [
    <ButtonGroup>
      <GraphStyleDropdown buttonSize="sm" />
      <PowerQueryActionsDropdown
        copyToClipboardHandler={copyToClipboardHandler}
        downloadFileHandler={downloadFileHandler}
        disableCopyAndDownload={disableCopyAndDownload}
      />
    </ButtonGroup>,
    <ButtonGroup>
      <TimeRangeDropdown buttonSize="sm" />
      <Button onClick={invokeQuery} variant={ButtonVariant.PRIMARY} testId={ELEMENT.powerQuerySearchButton} size="sm">
        Search
      </Button>
    </ButtonGroup>,
  ];

  return (
    <>
      <PowerQueryInput value={draftFilter} onPressEnter={invokeQuery} onInputValueChange={handleInputChange} />
      <div className="PowerQueries__Helpers">
        <Toolbar leftItems={leftItems} rightItems={rightItems} className="PowerQueries__ControlsToolbar" />
      </div>
    </>
  );
};

export default PowerQueryControls;
